import { ChangeEvent, FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material"
import { debounce } from "lodash"

import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { setAlert } from "../../../core/app/slices/alert/alertSlice"
import { IClinicalSettings } from "../../../core/app/slices/user/user.type"
import { updateClinicalSettings } from "../../../core/app/slices/user/userApis"
import { RootState } from "../../../core/app/store"
import { UI_PERMISSION } from "../../../core/utils/access/permissionConstants"
import usePermissions from "../../../hooks/userPermission"
import { UText } from "../../../ui-component"
import { SingleAttachment } from "../attachmentSetting/SingleAttachment"
import { GroupProtocol } from "../attachmentSetting/GroupProtocol"

const AttachmentStage: FC = () => {
  const { hasAny } = usePermissions()
  const [clinicalSetting, setClinicalSetting] = useState<IClinicalSettings>(
    {} as IClinicalSettings,
  )
  const { clinicalSettings } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [stageValue, setStageValue] = useState("")
  const [singleAttachmentOpen, setSingleAttachmentOpen] = useState(false)
  const [groupProtocolOpen, setGroupProtocolOpen] = useState(false)

  useEffect(() => {
    if (clinicalSettings) {
      setClinicalSetting(clinicalSettings)
      if (
        clinicalSettings.AttachmentStage !== undefined &&
        clinicalSettings.AttachmentStage !== null &&
        clinicalSettings.AttachmentStage !== 0
      ) {
        setStageValue(clinicalSettings.AttachmentStage.toString() || "")
        setClinicalSetting({
          ...clinicalSettings,
          AttachmentStage: 1,
        })
      }
    }
  }, [clinicalSettings])

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) return

    const value = parseInt(event.target.value)

    if (value === 0) {
      // "At beginning" selected - clear stage number and set AttachmentStage to 0
      setStageValue("")
      const obj = { AttachmentStage: 0 }
      setClinicalSetting({ ...clinicalSetting, ...obj })
      debouncedOnInput(obj)
    } else {
      // "At stage" selected - only save if there's an existing stage number
      const stageNum = parseInt(stageValue)
      if (stageNum && stageNum > 0) {
        const obj = { AttachmentStage: stageNum }
        setClinicalSetting({ ...clinicalSetting, ...obj })
        debouncedOnInput(obj)
      } else {
        // Just switch the radio visually, set to 1 for UI display only (not saved)
        setClinicalSetting({ ...clinicalSetting, AttachmentStage: 1 })
      }
    }
  }

  const handleStageNumberFocus = () => {
    // When user clicks into text field, automatically switch to "At stage" radio
    if (clinicalSetting.AttachmentStage === 0) {
      // Set to 1 temporarily for UI (radio selection), but don't save to backend
      setClinicalSetting({ ...clinicalSetting, AttachmentStage: 1 })
    }
  }

  const handleStageNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) return

    const value = event.target.value.replace(/[^0-9]/g, "")
    setStageValue(value)

    // Parse the number
    const stageNum = parseInt(value)

    if (stageNum && stageNum > 0) {
      // Valid number - update AttachmentStage and save to backend
      const obj = { AttachmentStage: stageNum }
      setClinicalSetting({ ...clinicalSetting, ...obj })
      debouncedOnInput(obj)
    }
  }

  const debouncedOnInput = debounce((obj) => {
    const data = { ...obj }
    if (hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) {
      dispatch(updateClinicalSettings(data)).then((res) => {
        if (res && res.payload && res.payload.response_code === 200) {
          setClinicalSetting((prev) => ({ ...prev, ...obj }))
        } else {
          dispatch(
            setAlert({
              message: t("clinicalSettings.dataUpdatedFailure"),
              isError: true,
            }),
          )
        }
      })
    }
  }, 500)

  return (
    <Box sx={{ width: "640px", p: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "center", pb: "16px" }}>
        <UText variant={"h4"}>{t("clinicalSettings.attachment")}</UText>
      </Box>
      <Box>
        <FormControl>
          <FormLabel
            sx={{
              color: (theme) => theme.palette.text.secondary,
              "&.Mui-focused": {
                color: (theme) => theme.palette.text.secondary,
              },
            }}
          >
            {t("clinicalSettings.defaultStagetoputonAttachments")}
          </FormLabel>
          <RadioGroup
            aria-labelledby="stage_attachments_label"
            name="AttachmentStage"
            value={clinicalSetting.AttachmentStage === 0 ? "0" : "1"}
            onChange={handleRadioChange}
            sx={{ px: 1, width: "fit-content" }}
          >
            <FormControlLabel
              sx={{
                color: (theme) => theme.palette.text.primary,
              }}
              value={"0"}
              control={<Radio />}
              label="At beginning of treatment"
            />
            <FormControlLabel
              sx={{
                color: (theme) => theme.palette.text.primary,
              }}
              value={"1"}
              control={<Radio />}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {"At stage"}
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    size={"small"}
                    value={stageValue}
                    onFocus={handleStageNumberFocus}
                    onChange={handleStageNumberChange}
                    sx={{
                      mx: 1,
                      width: "80px",
                    }}
                  />
                </Box>
              }
            />
          </RadioGroup>
          <Box sx={{ mt: "12px" }}>
            <Button
              variant="contained"
              sx={{ mr: 1 }}
              onClick={() => setSingleAttachmentOpen(true)}
            >
              single-attachment
            </Button>
            <Button
              variant="contained"
              onClick={() => setGroupProtocolOpen(true)}
            >
              group-protocol
            </Button>
          </Box>
        </FormControl>
      </Box>
      <SingleAttachment
        open={singleAttachmentOpen}
        onClose={() => setSingleAttachmentOpen(false)}
      />
      <GroupProtocol
        open={groupProtocolOpen}
        onClose={() => setGroupProtocolOpen(false)}
      />
    </Box>
  )
}

export default AttachmentStage
